import type { ChartType } from './ChartType';

/**
 * Configuration for a chart type.
 * Defines behavioral preferences for chart interactions.
 *
 * Calculation strategies (domain normalization, zoom) are determined by
 * scale type in SCALE_TYPE_REGISTRY (scaleTypes/registry.ts).
 */
export interface ChartTypeConfig {
  /** Whether to show crosshair when hovering over data points */
  showCrosshairOnDataPoints: boolean;
}

/**
 * Registry for chart type configurations.
 *
 * This defines chart-type-specific interaction behaviors:
 * - showCrosshairOnDataPoints: Whether crosshair appears when hovering data
 *
 * Default scale types are defined in constants.ts.
 * Calculation strategies are determined by SCALE_TYPE_REGISTRY based on xScaleType.
 *
 * When adding a new chart type:
 * 1. Add the type to ChartType union in chartTypes/ChartType.ts
 * 2. Add an entry to this registry
 */
export const CHART_TYPE_REGISTRY = {
  line: {
    showCrosshairOnDataPoints: true,
  },
  column: {
    showCrosshairOnDataPoints: false,
  },
} satisfies Record<ChartType, ChartTypeConfig>;

/**
 * Gets the configuration for a chart type.
 * @throws Error if chart type is not registered
 */
export function getChartTypeConfig(chartType: ChartType): ChartTypeConfig {
  const config = CHART_TYPE_REGISTRY[chartType];
  if (!config) {
    throw new Error(
      `Chart type '${chartType}' is not registered. ` +
        `Please add it to CHART_TYPE_REGISTRY in chartTypes/registry.ts`,
    );
  }
  return config;
}
